<template>
	<section class="account">
		<template
			v-if="initialized && isLoggedIn"
		>
			<div class="divider">
				<div class="divider__left">
					<div class="account__left">
						<div class="account__header insicht insicht--fade-up">
							<div class="account__header-user">
								<div class="account__header-user-avatar">
									<UserAvatar/>
								</div>
								<div class="account__header-user-details">
									<div
										class="account__header-user-name"
										v-sanitize="`${accountUserDetail.firstName} ${accountUserDetail.lastName}`"
									/>
									<div
										v-if="accountClubDetail && accountClubDetail.tier"
										class="account__header-user-level"
										v-sanitize="accountClubDetail.tier.label"
									/>
									<div
										v-if="accountClubDetail && accountClubDetail.tier"
										class="account__header-user-points"
										v-sanitize="pointsToNextText"
									/>
								</div>
							</div>
							<UserPoints
								v-if="accountClubDetail"
								:points="accountClubDetail.balance || 0"
							/>

							<div>
								<Button
									class="account__header-qrcode"
									type="button"
									level="primary"
									:text="$t('account.selforderingQR')"
									@click.prevent="onSelforderingQR"
									icon="qr-code"
									small
									reverse
								/>
							</div>
							<Button
								class="account__header-logout"
								type="button"
								level="tertiary"
								:text="$t('account.logoutButton')"
								@click.prevent="onLogout"
								arrow
							/>
						</div>
						<nav class="account__nav insicht insicht--fade-up">
							<ul class="account__nav-list">
								<template
									v-for="(area, index) in areas"
								>
									<template
										v-if="area.header && !area.disabled"
									>
										<li
											:key="index"
											class="account__nav-header"
											v-html="area.label"
										/>
									</template>
									<template
										v-else-if="!area.disabled"
									>
										<li
											:key="index"
											class="account__nav-item"
										>
											<a
												:ref="activeArea == area.id ? 'navItemActive' : 'navItem'"
												class="account__nav-item-link"
												:class="{
													'account__nav-item-link--active': activeArea == area.id
												}"
												v-html="area.label"
												@click.prevent="onNavClick(area)"
											/>
										</li>
									</template>
								</template>
							</ul>
						</nav>
					</div>
				</div>

				<div class="divider__right account__area">
					<section class="account__area-content insicht insicht--fade-up" v-if="activeArea == 'discounts'">
						<h1 class="account__area-title" v-html="$t('account.discountsTitle')"/>
						<div class="account__block">
							<h2 class="account__block-title" v-html="$t('account.couponsTitle')"/>
							<ul
								class="account__coupons"
								v-if="accountCoupons.length"
							>
								<li
									class="account__coupon"
									v-for="(coupon, index) in accountCoupons"
									:key="`coupon-${index}`"
								>
									<h3
										class="account__coupon-name"
										v-html="coupon.title"
									/>
									<ul class="account__coupon-info">
										<li class="account__coupon-info-item">
											<span
												class="account__coupon-info-item-title"
												v-html="$t('account.couponDiscount')"
											/>
											<span
												class="account__coupon-info-item-value"
												v-html="formatDiscount(coupon.discount)"
											/>
										</li>
										<li class="account__coupon-info-item">
											<span
												class="account__coupon-info-item-title"
												v-html="$t('account.couponCode')"
											/>
											<span
												class="account__coupon-info-item-value"
												v-html="coupon.code"
											/>
										</li>
										<li
											class="account__coupon-info-item"
											v-if="coupon.validUntil"
										>
											<span
												class="account__coupon-info-item-title"
												v-html="$t('account.couponExpiration')"
											/>
											<span
												class="account__coupon-info-item-value"
												v-html="formatDate(coupon.validUntil)"
											/>
										</li>
										<li
											class="account__coupon-info-item account__coupon-info-item--span"
											v-if="coupon.validity"
										>
											<span
												class="account__coupon-info-item-title"
												v-html="$t('account.couponRules')"
											/>
											<span
												class="account__coupon-info-item-value account__coupon-info-item-value--break"
												v-html="coupon.validity"
											/>
										</li>
									</ul>
								</li>
							</ul>
							<h3
								v-else
								class="account__block-empty"
								v-html="$t('account.noCoupons')"
							/>
						</div>

						<div
							class="account__block"
							v-if="accountUserDetail.groups && accountUserDetail.groups.length"
						>
							<h2 class="account__block-title" v-html="$t('account.groupsTitle')"/>
							<ul class="account__groups-list">
								<li
									class="account__groups-item"
									v-for="(group, index) in accountUserDetail.groups"
									:key="`${index}-${group.name}`"
									v-html="group.name"
								/>
							</ul>
						</div>

						<div
              v-if="hasFeature('FEATURE_POINZ') && (hasFeature('FEATURE_POINZ_REGISTER') || accountPoinzDetail.available)"
              class="account__block"
            >
							<h2 class="account__block-title">
									<span v-html="$t('account.poinzTitle')"/>
									<a
										class="account__block-title-icon"
										v-if="poinzText.faqUrl"
										:href="poinzText.faqUrl"
										target="_blank"
									>
										<SVGInfo/>
									</a>
							</h2>
              <GeneralMessage
                v-if="$te('account.poinzMessage')"
                :message="$t('account.poinzMessage')"
                type="promo"
                stick
              />
							<div
								class="account__poinz-info account__block-segments"
								v-if="accountPoinzDetail.available"
							>
								<div class="account__block-segment account__block-segment--large">
									<GeneralMessage
										v-if="poinzRegisterConfirm.message"
										:message="poinzRegisterConfirm.message"
										:type="poinzRegisterConfirm.type"
										@close="poinzRegisterConfirm.message = ''"
									/>
									<ul class="account__poinz-grid">
										<li
											v-for="n in accountPoinzDetail.perCard"
											:key="`poinz-${n}`"
											class="account__poinz-grid-item"
											:class="{
												'account__poinz-grid-item--checked': n <= poinzCountCurrent
											}"
										>
											<SVGCheckmark v-if="n <= poinzCountCurrent"/>
										</li>
									</ul>
								</div>
								<div class="account__block-segment">
									<h3
										class="account__poinz-title"
										v-html="$t('account.poinzCount', {count: poinzCountCurrent, perCard: accountPoinzDetail.perCard})"
									/>
									<GeneralMessage
										v-if="poinzResponse.message"
										:message="poinzResponse.message"
										:type="poinzResponse.type"
										stick
									/>
									<div class="account__poinz-text">
										<p v-html="$t('account.poinzCouponHint')"/>
									</div>
									<h4
										class="account__poinz-subtitle"
										v-html="$t('account.poinzCountCards', {count: poinzCountCards})"
										v-if="poinzCountCards"
									/>
									<Button
										type="button"
										level="secondary"
										small
										:text="$t('account.poinzCouponButton')"
										:disabled="loading || accountPoinzDetail.count < accountPoinzDetail.perCard"
										@click.prevent="onPoinzRedeem"
									/>
								</div>
							</div>
							<div
                v-else-if="hasFeature('FEATURE_POINZ_REGISTER')"
								class="account__poinz-register account__block-segments"
							>
								<div class="account__block-segment" v-if="poinzText.column1">
									<div class="account__poinz-text" v-html="poinzText.column1" />
								</div>
								<div class="account__block-segment" v-if="poinzText.column2">
									<div class="account__poinz-text" v-html="poinzText.column2" />
								</div>
								<div class="account__block-segment">
									<ValidationObserver
										ref="poinzForm"
										v-slot="{ invalid, errors }"
										tag="form"
										class="account__poinz-form"
										@submit.prevent="onPoinzSubmit"
										novalidate
									>
										<GeneralMessage
											v-if="poinzResponse.message"
											:message="poinzResponse.message"
											:type="poinzResponse.type"
											stick
										/>
										<ValidationProvider
											rules="required"
											name="birthday"
											tag="div"
											v-slot="{ invalid, errors, validated }"
											:customMessages="{ required: $t('forms.errors.date') }"
										>
											<FormInputDate
												:placeholder="$t('forms.labels.birthday')"
												:label="$t('forms.labels.birthday')"
												v-model="poinzForm.birthday"
												:initialValue="poinzForm.birthday"
												min-date=""
												max-date="today"
												required
												:disabled="loading"
											/>
											<span
												class="input-error"
												v-if="invalid && validated"
												v-html="errors[0]"
											/>
										</ValidationProvider>

										<ValidationProvider
											rules="required"
											name="gender"
											tag="div"
											v-slot="{ invalid, errors, validated }"
											:customMessages="{ required: $t('forms.errors.gender') }"
										>
											<FormInputRadio
												:legend="$t('forms.labels.salutation')"
												name="gender"
												:options="[
													{
														value: 'm',
														label: $t('forms.labels.salutationMale'),
													},
													{
														value: 'f',
														label: $t('forms.labels.salutationFemale'),
													}
												]"
												v-model="poinzForm.gender"
												:disabled="loading"
											/>
											<span
												class="input-error"
												v-if="invalid && validated"
												v-html="errors[0]"
											/>
										</ValidationProvider>

										<Button
											type="submit"
											level="primary"
											small
											:text="$t('account.poinzRegisterButton')"
											:disabled="loading"
										/>
									</ValidationObserver>

								</div>
							</div>
						</div>
					</section>

					<section class="account__area-content insicht insicht--fade-up" v-if="activeArea == 'orders'">
						<h1 class="account__area-title" v-html="$t('account.ordersTitle')"/>
						<div
							class="account__block"
							v-if="hasBasket && isLoggedIn && lastOrdersFiltered.length"
						>
							<div class="account__last-orders last-orders-teaser">
								<SVGBag class="last-orders-teaser__icon"/>
								<div class="last-orders-teaser__content">
									<div
										class="last-orders-teaser__text"
										v-html="$t('shoplist.lastOrders.teaserText')"
									/>
									<Button
										class="last-orders-teaser__button"
										level="tertiary"
										:text="$t('shoplist.lastOrders.teaserButton')"
										type="button"
										arrow
										@click.prevent="onLastOrders"
									/>
								</div>
							</div>
						</div>
						<div class="account__block account__block--flex">
              <template v-if="hasFeature('FEATURE_LIVE_ORDER_STATE')">
                <div
                  v-show="notimeOrderStates.length"
                  class="account__block--flex-small"
                >
                  <h2
                    class="account__block-title"
                    v-html="$t('account.currentOrdersTitle')"
                  />
                  <LiveOrderStates />
                </div>
              </template>
							<div class="account__block--flex-fill">
								<h2
									class="account__block-title"
									v-html="$t('account.pastOrdersTitle')"
								/>
								<table
									class="account__orders"
									v-if="lastOrders.length"
								>
									<tr class="account__orders-header">
										<th
											class="account__orders-header-col"
											v-html="$t('account.orderNumber')"
										/>
										<th
											class="account__orders-header-col"
											v-html="$t('account.orderDate')"
										/>
										<th
											class="account__orders-header-col"
											v-html="$t('account.orderTotal')"
										/>
									</tr>
									<tr
										class="account__orders-row"
										v-for="order in lastOrders"
										:key="order.token"
									>
										<td
											class="account__orders-row-col"
											:data-header="$t('account.orderNumber')"
										>
											<a
												class="account__orders-row-col-link"
												:href="order.url"
												target="_blank"
												v-html="order.number"
											/>
										</td>
										<td
											class="account__orders-row-col"
											v-html="formatDate(order.checkoutCompletedAt)"
											:data-header="$t('account.orderDate')"
										/>
										<td
											class="account__orders-row-col"
											v-html="$options.filters.fwgpricecent(order.total, 'html')"
											:data-header="$t('account.orderTotal')"
										/>
									</tr>
								</table>
								<h3
									v-else
									class="account__block-empty"
									v-html="$t('account.noOrders')"
								/>
							</div>
						</div>
					</section>

					<section class="account__area-content insicht insicht--fade-up" v-if="activeArea == 'reservations'">
						<h1 class="account__area-title" v-html="$t('account.reservations.title')"/>
						<div class="account__block">
							<ul class="account__reservations" v-if="accountReservations.length">
								<li
									v-for="reservation in accountReservations"
									:key="reservation.id"
									class="account__reservation"
								>
									<h3
										class="account__reservation-date"
										:class="{
											'account__reservation-date--canceled': reservation.canceled
										}"
										v-html="reservationDate(reservation)"
									/>
									<span
										class="account__reservation-restaurant"
										v-html="reservationRestaurant(reservation)"
									/>
									<span
										class="account__reservation-people"
										v-html="reservationPeople(reservation)"
									/>
									<span
										v-if="reservation.canceled"
										class="account__reservation-status"
										v-html="$t('account.reservations.canceled')"
									/>
									<div
										v-if="reservation.editable"
										class="account__reservation-buttons"
									>
										<Button
											class="account__reservation-button"
											type="button"
											:title="$t('account.reservations.cancelButton')"
											level="secondary"
											@click="onReservationCancel(reservation)"
											icon="trash"
											small
										/>
										<Button
											v-if="!reservation.indining"
											class="account__reservation-button"
											type="button"
											:title="$t('account.reservations.editButton')"
											level="primary"
											@click="onReservationEdit(reservation)"
											icon="edit"
											small
										/>
									</div>
								</li>

							</ul>
							<h3
								v-else
								class="account__block-empty"
								v-html="$t('account.reservations.empty')"
							/>
						</div>
					</section>

					<section class="account__area-content insicht insicht--fade-up" v-if="activeArea == 'address'">
						<h1 class="account__area-title" v-html="$t('account.addressTitle')"/>
						<div class="account__block">
							<h3
								class="account__block-subtitle"
								v-html="$t('account.addressSubTitle')"
							/>
							<address
								class="account__address"
								v-sanitize="formattedAddress"
								v-if="formattedAddress"
							/>
							<div
								class="account__address"
								v-else
								v-html="$t('account.noAddress')"
							/>
							<Button
								class="account__edit-button"
								type="button"
								level="tertiary"
								:text="$t('account.addressEdit.edit')"
								@click.prevent="onAddressEdit"
								arrow
							/>
						</div>
					</section>

					<section class="account__area-content insicht insicht--fade-up" v-if="activeArea == 'personal'">
						<h1 class="account__area-title" v-html="$t('account.personalTitle')"/>
						<div class="account__block">
							<h2
								class="account__block-title"
								v-html="$t('account.userDetailTitle')"
							/>
							<address
								class="account__address"
								v-sanitize="userDetailsFormatted"
							/>
							<Button
								class="account__edit-button"
								type="button"
								level="tertiary"
								:text="$t('account.userDetailEdit.edit')"
								@click.prevent="onUserDetailEdit"
								arrow
							/>
						</div>

						<div class="account__block">
							<h2 class="account__block-title" v-html="$t('account.passwordResetTitle')"/>
							<div class="account__block-text" v-html="$t('account.passwordResetText')"/>
							<Button
								class="account__edit-button"
								type="button"
								level="tertiary"
								:text="$t('account.passwordResetButton')"
								@click.prevent="onPasswordChange"
								arrow
							/>
						</div>

						<div
							class="account__block"
						>
							<h2 class="account__block-title" v-html="$t('account.deleteTitle')"/>
							<div class="account__block-text" v-html="$t('account.deleteText')"/>
							<Button
								class="account__edit-button"
								type="button"
								:text="$t('account.deleteButton')"
								level="tertiary"
								@click="onDelete"
								arrow
							/>
						</div>
					</section>


					<section class="account__area-content insicht insicht--fade-up" v-if="activeArea == 'referral'">
						<AccountReferral/>
					</section>
				</div>
			</div>
		</template>

		<div
			class="account__wrapper insicht insicht--fade-up"
			v-else-if="initialized && !isLoggedIn"
		>
			<div class="text-block text-block--center">
				<h1 class="text-block__title" v-html="$t('account.notLoggedIn.title')"/>
				<div class="text-block__text">
					<p v-html="$t('account.notLoggedIn.text')"/>
				</div>
				<Button
					level="primary"
					:text="$t('account.notLoggedIn.button')"
					@click="onLogin"
				/>
			</div>
		</div>

		<Modal
			ref="qrModal"
			overlay-type="selfordering-qr"
			:html-classes="['hide--header', 'show--modal']"
			narrow
		>
			<OverlaySelforderingQR
				@close="onAddressEditClose"
			/>
		</Modal>

		<Modal
			ref="userDetailModal"
			overlay-type="user-detail-edit"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			soft-close
			@softclose="onUserDetailEditSoftClose"
		>

			<OverlayUserDetailEdit
				ref="overlayUserDetailEdit"
				@close="onUserDetailEditClose"
			/>

		</Modal>

		<Modal
			ref="addressModal"
			overlay-type="address-edit"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			soft-close
			@softclose="onAddressEditSoftClose"
		>

			<OverlayAddressEdit
				ref="overlayAddressEdit"
				@close="onAddressEditClose"
			/>

		</Modal>

		<Modal
			ref="passwordModal"
			overlay-type="password-change"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			soft-close
			@softclose="onPasswordChangeSoftClose"
		>
			<div class="overlay-dialog">
				<FormPasswordReset
					ref="passwordChange"
				/>
			</div>
		</Modal>

	</section>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import veevalidate from "@/helpers/veevalidate"
import GenericMixin from '@/mixins/GenericVueMixin'
import Collapsible from '@/components/Collapsible'
import FormInputDate from '@/components/FormInputDate'
import FormInputRadio from '@/components/FormInputRadio'
import FormPasswordReset from '@/components/FormPasswordReset'
import AccountReferral from '@/components/AccountReferral'
import UserAvatar from '@/components/UserAvatar'
import UserPoints from '@/components/UserPoints'
import Button from '@/components/Button'
import Modal from '@/components/Modal'
import GeneralMessage from '@/components/GeneralMessage'
import OverlayAddressEdit from '@/pages/OverlayAddressEdit'
import OverlaySelforderingQR from '@/pages/OverlaySelforderingQR'
import OverlayUserDetailEdit from '@/pages/OverlayUserDetailEdit'
import leadingZero from '@/utils/leadingZero'
import sleep from '@/utils/sleep'
import htmlComponent from "htmlcomponent";
import {eventhub, EVENT_ACCOUNT_ISLOGGEDIN} from '@/store/eventhub'
import scrrroll from 'scrrroll'
import { dayjsLocal } from "@/helpers/dayjs";
import urlWatcher from '@/helpers/urlwatcher'
import LiveOrderStates from "@/components/LiveOrderStates"

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationObserver,
		ValidationProvider,
		FormInputDate,
		FormInputRadio,
		FormPasswordReset,
		Button,
		Modal,
		GeneralMessage,
		OverlayAddressEdit,
		OverlaySelforderingQR,
		OverlayUserDetailEdit,
		AccountReferral,
		Collapsible,
		UserAvatar,
		UserPoints,
		LiveOrderStates,
	},
	data() {
		return {
			initialized: false,
			poinzText: {
				column1: '',
				column2: '',
				faqUrl: '',
			},
			poinzForm: {
				gender: '',
				birthday: '',
			},
			poinzResponse: {
				message: '',
				type: '',
			},
			poinzRegisterConfirm: {
				message: '',
				type: '',
			},
			activeArea: '',
			areas: [
				// {
				// 	label: this.$t('account.accountTitle'),
				// 	header: true,
				// },
				{
					label: this.$t('account.discountsTitle'),
					id: 'discounts',
				},
				{
					label: this.$t('account.ordersTitle'),
					id: 'orders',
				},
				{
					label: this.$t('account.reservations.title'),
					id: 'reservations',
				},
				{
					label: this.$t('account.addressTitle'),
					id: 'address',
				},
				{
					label: this.$t('account.personalTitle'),
					id: 'personal',
				},
				{
					label: this.$t('account.referralTitle'),
					id: 'referral',
				},
			],
			scrolled: false,
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"lastOrders",
			"accountLastAddress",
			"accountUserDetail",
			"accountPoinzDetail",
			"accountClubDetail",
			"accountCoupons",
			"accountReferralUrl",
			"accountReservations",
		]),
		...mapGetters("cart", [
			"shippingMethod",
			'hasBasket',
		]),
		...mapGetters("productlist", [
			"favoriteCode",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters('order', [
			'notimeOrderStates',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		formattedAddress() {
			if (this.accountLastAddress) {

				let address = [
					`${this.accountLastAddress.firstName} ${this.accountLastAddress.lastName}`
				];

				if (this.accountLastAddress.street || this.accountLastAddress.streetNumber) {
					address.push(`${this.accountLastAddress.street || ''} ${this.accountLastAddress.streetNumber || ''}`);
				}

				if (this.accountLastAddress.postcode || this.accountLastAddress.city) {
					address.push(`${this.accountLastAddress.postcode || ''} ${this.accountLastAddress.city || ''}`);
				}

				if (this.accountLastAddress.phoneNumber) {
					address.push(`☎ ${this.accountLastAddress.phoneNumber}`);
				}

				if (this.accountLastAddress.company) {
					address.unshift(this.accountLastAddress.company);
				}

				// if (this.accountLastAddress.details) {
				// 	address.push(`<small>${this.accountLastAddress.details}</small>`);
				// }

				return address.join('<br>');
			}
		},
		userDetailsFormatted() {
			let details = [
				`${this.accountUserDetail.firstName} ${this.accountUserDetail.lastName}`,
				this.accountUserDetail.email,
			];

			if (this.accountUserDetail.phoneNumber) {
				details.push(`☎ ${this.accountUserDetail.phoneNumber}`);
			}

			return details.join('<br>');
		},
		poinzCountCards() {
			return Math.floor(this.accountPoinzDetail.count / this.accountPoinzDetail.perCard);
		},
		poinzCountCurrent() {
			return this.accountPoinzDetail.count
				? this.accountPoinzDetail.count % this.accountPoinzDetail.perCard || 10
				: 0;
		},
		lastOrdersFiltered() {
			return this.lastOrders
				.filter(order => order.shipmentCode === this.shippingMethod.code)
				.slice(0,3);
		},
		pointsToNextText() {
			if (this.accountClubDetail?.tier?.tier < this.accountClubDetail?.nextTier?.tier) {
				return this.$t('club.levels.pointsToNext', { points: this.accountClubDetail.pointsToNext, nextTier: this.accountClubDetail.nextTier.tier })
			}

			return this.$t('club.levels.pointsToNextHighest')
		},
	},
	methods: {
		onLogin() {
			htmlComponent.send('overlay:open', {type: 'login'});
		},
		async onLogout() {
			const confirmed = await this.$store.dispatch('dialog/showDialog', {
				title: this.$t('account.logoutDialogTitle'),
				message: this.$t('account.logoutDialogText'),
				confirm: this.$t('account.logoutDialogConfirm'),
				cancel: this.$t('account.logoutDialogCancel'),
			});
			if (confirmed) {
				this.loading = true;
				this.$store.dispatch('logout');
			}
		},
		onSelforderingQR() {
			this.$refs.qrModal.open();
		},
		async onDelete() {
			const confirmed = await this.$store.dispatch('dialog/showDialog', {
				title: this.$t('account.deleteDialogTitle'),
				message: this.$t('account.deleteDialogText'),
				confirm: this.$t('account.deleteDialogConfirm'),
				cancel: this.$t('account.deleteDialogCancel'),
			});
			if (confirmed) {
				this.loading = true;
				this.$store.dispatch('account/delete');
				this.loading = false;
			}
		},
		onUserDetailEdit() {
			this.$refs.userDetailModal.open();
		},
		onUserDetailEditSoftClose() {
			this.$refs.overlayUserDetailEdit.onClose();
		},
		onUserDetailEditClose() {
			this.$refs.userDetailModal.close();
		},
		onAddressEdit() {
			this.$refs.addressModal.open();
		},
		onAddressEditSoftClose() {
			this.$refs.overlayAddressEdit.onClose();
		},
		onAddressEditClose() {
			this.$refs.addressModal.close();
		},
		onPasswordChange() {
			this.$refs.passwordModal.open();
		},
		async onPasswordChangeSoftClose() {
			if (this.$refs.passwordChange.edited) {
				const confirmed = await this.$store.dispatch('dialog/showDialog', {
					title: this.$t('account.resetPassword.dialogTitle'),
					message: this.$t('account.resetPassword.dialogMessage'),
					confirm: this.$t('account.resetPassword.dialogConfirm'),
					cancel: this.$t('account.resetPassword.dialogCancel'),
				});
				if (confirmed) {
					return;
				}
			}
			this.$refs.passwordModal.close();
		},
		async onPoinzRedeem() {
			if (this.accountPoinzDetail.count >= this.accountPoinzDetail.perCard && !this.loading) {
				this.loading = true;
				try {
					await this.$store.dispatch('poinzRedeem')
					this.poinzResponse.message = '';
					this.poinzResponse.type = '';
				} catch(err) {
					let message = err.response.data ? err.response.data.message : this.$t('forms.errors.generic');
					this.poinzResponse.message = message;
					this.poinzResponse.type = 'error';
				}
				this.loading = false;
			}
		},
		async onPoinzSubmit(e) {
			const isValid = await this.$refs.poinzForm.validate()

			if (isValid && !this.loading) {
				this.loading = true;
				try {
					await this.$store.dispatch('poinzRegister', this.poinzForm)
					this.poinzResponse.message = '';
					this.poinzResponse.type = '';
					this.poinzRegisterConfirm.message = this.$t('account.successPoinzRegister');
					this.poinzRegisterConfirm.type = 'info';
				} catch(err) {
					let message = err.response.data ? err.response.data.message : this.$t('forms.errors.generic');
					this.poinzResponse.message = message;
					this.poinzResponse.type = 'error';
				}
				this.loading = false;
			}
		},
		formatDate(date) {
			const d = new Date(date);
			const day = leadingZero(d.getDate());
			const month = leadingZero(d.getMonth() + 1);
			const year = d.getFullYear();

			return `${day}.${month}.${year}`;
		},
		formatDiscount(discount) {
			discount = '' + discount;
			if (discount.indexOf('%') < 0) {
				// multiply by 100 because coupon is in francs rather than cents
				discount = this.$options.filters.fwgpricecent(discount * 100, 'html')
			}
			return discount;
		},
		readPoinzText() {
			let data = this.$root.options.poinzText || {};
			try {
				const jsonData = JSON.parse(document.querySelector('#poinz-account-text').innerHTML);
				data = {
					...data,
					...jsonData,
				};
			}
			catch(e) {
				// no data found
			}

			if (data) {
				Object.keys(data).forEach(key => {
					Vue.set(this.poinzText, key, data[key]);
				});
			}
		},
		async onIsLoggedIn() {
			await this.$nextTick();
		},
		reservationDate(reservation) {
			const date = dayjsLocal((new Date(reservation.startSec * 1000)));
			const output = [
				date.format('L'),
			];

			if (reservation.duration) {
				output.push(`${date.format('HH:mm')} - ${date.add(reservation.duration, 'minute').format('HH:mm')}`);
			}
			else {
				output.push(date.format('HH:mm'));
			}

			return output.join(' ');
		},
		reservationRestaurant(reservation) {
			let restaurant = reservation.name;

			if (reservation.roomName) {
				restaurant += `, ${reservation.roomName}`;
			}

			return restaurant;
		},
		reservationPeople(reservation) {
			return `${reservation.firstName} ${reservation.lastName}, ${this.$tc('account.reservations.people', reservation.seats, { count: reservation.seats })}`;
		},
		onReservationEdit(reservation) {
			this.$store.dispatch('redirect', {
				name: 'reservation-edit',
				params: {
					reservationHash: reservation.reservationHash
				}
			})
		},
		async onReservationCancel(reservation) {
			const confirmed = await this.$store.dispatch('dialog/showDialog', {
				title: this.$t('account.reservations.cancelDialogTitle'),
				message: this.$t('account.reservations.cancelDialogMessage'),
				confirm: this.$t('account.reservations.cancelDialogConfirm'),
				cancel: this.$t('account.reservations.cancelDialogCancel'),
			});
			if (confirmed) {
				this.loading = true;
				await this.$store.dispatch('reservation/cancelReservation', { reservation: reservation.reservationHash });
				await this.$store.dispatch('account/loadReservations');
				this.loading = false;
			}
		},
		onLastOrders() {
			this.$store.dispatch('redirect', { name: 'shop-taxon', params: { taxon: this.favoriteCode }, force: true})
		},
		async onHashChange(params) {
			if (params.area) {
				this.activeArea = params.area;

				if (this.isMobile()) {
					if (this.$refs.navItemActive?.length) {
						await this.$nextTick();
						this.$refs.navItemActive[0].scrollIntoView(false);
					}
				}
			}
		},
		onNavClick(item) {
			urlWatcher.change({area: item.id});
		}
	},
	async created() {
		this.loading = true;
		try {
			await this.$store.dispatch("initializeAccount")
			this.initialized = true;
		}
		catch(err) {
			this.$store.dispatch('dialog/showDialog', {
				title: this.$t('forms.errors.error'),
				message: this.$t('forms.errors.generic'),
				confirm: 'OK',
			});
		}
		this.loading = false;
		await this.$nextTick();
		if (!this.isLoggedIn) {
			this.onLogin();
		}
	},
	async updated() {
		await this.$nextTick();
	},
	async mounted() {
		urlWatcher.addListener(this.onHashChange)
		eventhub.$on(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)
		this.readPoinzText();
		await this.$nextTick();
		const params = urlWatcher.readUrl()
		if (!params.area) {
			const item = this.areas.find(area => area.id && !area.disabled)
			if (item) {
				this.onNavClick(item);
			}
		}
		else {
			this.onHashChange(params);
		}
	},
	destroyed() {
		urlWatcher.removeListener(this.onHashChange)
		eventhub.$off(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)
	},
};
</script>

<style>
</style>
